/**
 * Validates URLs before navigating, to prevent open-redirect attacks
 * (CWE-601) when a destination is sourced from an API response, browser
 * storage, or any other taintable input.
 *
 * Validation enforces:
 *   - URL is parseable
 *   - scheme is http or https only (rejects javascript:, data:, vbscript:, file:)
 *   - no embedded credentials (https://user:pass@host)
 *   - host is same-origin OR in the allowlist OR explicitly opted in via allowExternal
 *
 * For SSO/IdP flows, the destination is org-configured and stored
 * server-side; pass allowExternal=true to bypass the host allowlist
 * while still enforcing scheme + no-credentials.
 */
export class SafeRedirect {
    /**
     * Hostnames (or suffixes prefixed with ".") permitted as redirect targets.
     * Same-origin is always allowed in addition to this list.
     */
    private static readonly ALLOWED_HOSTS: readonly string[] = [
        'welltrack.com',
        '.welltrack.com',
        'welltrack-boost.com',
        '.welltrack-boost.com',
    ];

    /**
     * Returns the validated URL string on success, or null if the URL
     * fails any safety check. Resolves relative URLs against the current
     * window.location.origin.
     */
    static validate(rawUrl: string | null | undefined, allowExternal = false): string | null {
        if (!rawUrl) return null;

        let url: URL;
        try {
            url = new URL(rawUrl, window.location.origin);
        } catch {
            return null;
        }

        if (url.protocol !== 'https:' && url.protocol !== 'http:') {
            return null;
        }

        if (url.username || url.password) {
            return null;
        }

        if (url.origin === window.location.origin) {
            return url.href;
        }

        const host = url.hostname.toLowerCase();
        const matchesAllowlist = SafeRedirect.ALLOWED_HOSTS.some((entry) => {
            if (entry.startsWith('.')) {
                return host.endsWith(entry) || host === entry.slice(1);
            }
            return host === entry;
        });

        if (matchesAllowlist) {
            return url.href;
        }

        if (allowExternal) {
            return url.href;
        }

        return null;
    }

    /**
     * Validate and assign to window.location.href. Returns true on success.
     */
    static go(rawUrl: string | null | undefined, allowExternal = false): boolean {
        const validated = SafeRedirect.validate(rawUrl, allowExternal);
        if (validated === null) {
            console.warn('SafeRedirect.go: refused unsafe URL', rawUrl);
            return false;
        }
        window.location.href = validated;
        return true;
    }

    /**
     * Validate and call window.open. Returns the new window or null.
     */
    static open(
        rawUrl: string | null | undefined,
        target = '_blank',
        allowExternal = false
    ): Window | null {
        const validated = SafeRedirect.validate(rawUrl, allowExternal);
        if (validated === null) {
            console.warn('SafeRedirect.open: refused unsafe URL', rawUrl);
            return null;
        }
        return window.open(validated, target);
    }
}
